/**
 * The close checklist for one month, and the four transitions.
 *
 * Overriding a blocking check needs a written reason typed here, which is
 * recorded against the period and audited — so "why does March show ₹40,000 in
 * suspense" has an answer six months later.
 */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Checklist, type Period } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'

export default function PeriodDetail() {
  const { label = '' } = useParams()
  const { can } = useAuth()
  const queryClient = useQueryClient()

  const checklist = useQuery({
    queryKey: ['checklist', label],
    queryFn: () => api.get<Checklist>(`/periods/${label}/checklist`),
    // Derived server-side on every request; caching it here would recreate
    // exactly the staleness the server-side design avoids.
    staleTime: 0,
  })

  const [overrides, setOverrides] = useState<Record<string, string>>({})
  const [reopenReason, setReopenReason] = useState('')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['checklist', label] })
    void queryClient.invalidateQueries({ queryKey: ['periods'] })
  }

  const close = useMutation({
    mutationFn: () => api.post<Checklist>(`/periods/${label}/close`, { overrides }),
    onSuccess: () => {
      setOverrides({})
      refresh()
    },
  })
  const review = useMutation({
    mutationFn: () => api.post<Period>(`/periods/${label}/review`),
    onSuccess: refresh,
  })
  const reopen = useMutation({
    mutationFn: () => api.post<Period>(`/periods/${label}/reopen`, { reason: reopenReason }),
    onSuccess: () => {
      setReopenReason('')
      refresh()
    },
  })
  const lock = useMutation({
    mutationFn: () => api.post<Period>(`/periods/${label}/lock`),
    onSuccess: refresh,
  })

  if (checklist.isLoading) return <Loading what={`Running the ${label} checklist`} />
  if (checklist.error) return <ErrorNote error={checklist.error} />

  const data = checklist.data!
  const blocking = data.checks.filter((c) => c.severity === 'blocking' && !c.passed)
  const advisory = data.checks.filter((c) => c.severity === 'advisory' && !c.passed)
  const canClose = can('gl.period.close')
  const canLock = can('gl.period.lock')
  const unexplained = blocking.filter((c) => !(overrides[c.key] ?? '').trim())

  return (
    <>
      <Card
        title={`${data.period_label}`}
        hint={<Pill value={data.status} />}
        actions={
          <div className="row">
            {canClose && data.status === 'open' && (
              <button className="shrink" onClick={() => review.mutate()} disabled={review.isPending}>
                Begin review
              </button>
            )}
            {canClose && (data.status === 'open' || data.status === 'under_review') && (
              <button
                className="primary shrink"
                onClick={() => close.mutate()}
                disabled={close.isPending || unexplained.length > 0}
                title={
                  unexplained.length > 0
                    ? 'Give a reason for each blocking item first'
                    : undefined
                }
              >
                {close.isPending ? 'Closing…' : 'Close month'}
              </button>
            )}
            {canLock && data.status === 'closed' && (
              <button className="shrink" onClick={() => lock.mutate()} disabled={lock.isPending}>
                Lock permanently
              </button>
            )}
          </div>
        }
      >
        <ErrorNote error={close.error ?? review.error ?? lock.error} />
        {data.ready_to_close ? (
          <Note kind="ok">Everything blocking has passed. This month is ready to close.</Note>
        ) : (
          <Note kind="warn">
            {blocking.length} blocking item{blocking.length === 1 ? '' : 's'} to resolve, or
            override with a written reason.
          </Note>
        )}
        {advisory.length > 0 && (
          <Note kind="info">
            {advisory.length} advisory item{advisory.length === 1 ? '' : 's'} — worth a look,
            but they do not stop the close.
          </Note>
        )}
        <p className="muted" style={{ marginBottom: 0 }}>
          This checklist ran just now. It is never stored: a cached “all clear” from last
          week stays green while the books move underneath it.
        </p>
      </Card>

      <Card title="Checks" tight>
        <table>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Result</th>
              <th>Check</th>
              <th>What it found</th>
            </tr>
          </thead>
          <tbody>
            {data.checks.map((c) => (
              <tr key={c.key}>
                <td>
                  <Pill value={c.passed ? 'pass' : c.severity} />
                </td>
                <td>
                  {c.title}
                  <div className="faint mono">{c.key}</div>
                </td>
                <td className="muted">
                  {c.passed ? <span className="faint">—</span> : c.detail}
                  {!c.passed && c.severity === 'blocking' && canClose && (
                    <div style={{ marginTop: 8 }}>
                      <Field label="Close anyway, because…">
                        <input
                          value={overrides[c.key] ?? ''}
                          placeholder="written reason, kept against this period"
                          onChange={(e) =>
                            setOverrides({ ...overrides, [c.key]: e.target.value })
                          }
                        />
                      </Field>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {canClose && (data.status === 'closed' || data.status === 'under_review') && (
        <Card title="Reopen this month">
          <ErrorNote error={reopen.error} />
          <p className="muted">
            Reopening lands in <em>under review</em>, not open: the month was closed once, so
            it is being corrected rather than worked on afresh. The count is kept, and the
            reason goes into the audit trail.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              reopen.mutate()
            }}
          >
            <div className="row">
              <Field label="Reason">
                <input
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  required
                  placeholder="e.g. a supplier bill arrived after close"
                />
              </Field>
              <div className="shrink">
                <button type="submit" disabled={reopen.isPending || !reopenReason.trim()}>
                  Reopen
                </button>
              </div>
            </div>
          </form>
        </Card>
      )}

      {data.status === 'locked' && (
        <Note kind="info">
          This month is locked. Returns were filed on these figures, so there is no route
          back — correct it with an entry in an open month, which is what a revised return
          is.
        </Note>
      )}
    </>
  )
}
